import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Typography,
  IconButton,
  Avatar,
  Chip,
  Paper,
  TextField,
  List,
  ListItem,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Close,
  Send,
  AttachFile,
  CheckCircle,
  Schedule,
  Cancel,
  Visibility,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const ProjectDetailsPage = ({ open, onClose, projectId }) => {
  const messagesEndRef = useRef(null);
  const [user, setUser] = useState(null);
  const [project, setProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);

  const loadProjectDetails = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_URL}/projects/${projectId}/`, {
        headers: { Authorization: `Token ${token}` }
      });
      setProject(response.data);
    } catch (error) {
      console.error('Error loading project details:', error);
    }
  }, [projectId]);

  const loadMessages = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_URL}/projects/${projectId}/messages/`, {
        headers: { Authorization: `Token ${token}` }
      });
      const msgs = response.data.results || response.data || [];
      setMessages(msgs);
      
      // Mark as read
      await axios.post(`${API_URL}/projects/${projectId}/messages/mark-read/`, {}, {
        headers: { Authorization: `Token ${token}` }
      });
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  }, [projectId]);

  useEffect(() => {
    const userInfo = localStorage.getItem('userInfo');
    if (userInfo) {
      setUser(JSON.parse(userInfo));
    }

    if (projectId && open) {
      loadProjectDetails();
      loadMessages();
    }
  }, [projectId, open, loadProjectDetails, loadMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !selectedFile) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      const formData = new FormData();
      formData.append('content', newMessage);
      if (selectedFile) {
        formData.append('attachment', selectedFile);
      }

      await axios.post(
        `${API_URL}/projects/${projectId}/messages/`,
        formData,
        { 
          headers: { 
            Authorization: `Token ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      setNewMessage('');
      setSelectedFile(null);
      loadMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'submitted': '#4caf50',
      'under_review': '#ff9800',
      'approved': '#2196f3',
      'rejected': '#f44336',
      'completed': '#9c27b0'
    };
    return colors[status] || '#757575';
  };

  const getDocumentStatusIcon = (status) => {
    if (status === 'approved') return <CheckCircle sx={{ color: '#4caf50' }} />;
    if (status === 'under_review') return <Schedule sx={{ color: '#ff9800' }} />;
    return <Cancel sx={{ color: '#f44336' }} />;
  };

  const capitalizeWords = (str) => {
    if (!str) return '';
    return str.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  if (!project) return null;

  return (
    <>
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { height: '90vh', borderRadius: 3 }
      }}
    >
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {project.name}
                </Typography>
                <Chip 
                  label={capitalizeWords(project.status)} 
                  sx={{ 
                    bgcolor: getStatusColor(project.status),
                    color: 'white',
                    fontWeight: 600
                  }} 
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Project ID: #{project.id} • Created {new Date(project.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </Typography>
            </Box>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left Panel - Project Details */}
          <Box sx={{ width: '45%', p: 3, overflowY: 'auto', borderRight: '1px solid #e0e0e0' }}>
            {/* Investment & Consultant */}
            <Paper sx={{ p: 2.5, mb: 2, bgcolor: '#f8f9fa' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                TOTAL INVESTMENT
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2', mb: 2 }}>
                ₹{project.investment_amount?.toLocaleString('en-IN')}
              </Typography>
              
              {project.consultant_name && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                  <Avatar sx={{ bgcolor: '#ff9800', width: 36, height: 36 }}>
                    {project.consultant_name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {capitalizeWords(project.consultant_name)} (Consultant)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ASSIGNED CONSULTANT
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>

            {/* Project Type */}
            <Paper sx={{ p: 2.5, mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                PROJECT TYPE
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {project.project_type || 'Not specified'}
              </Typography>
            </Paper>

            {/* Industry Type */}
            <Paper sx={{ p: 2.5, mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                INDUSTRY TYPE
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {project.industry_type || 'Not specified'}
              </Typography>
            </Paper>

            {/* Services Requested */}
            {project.services && project.services.length > 0 && (
              <Paper sx={{ p: 2.5, mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1.5, display: 'block' }}>
                  SERVICES REQUESTED
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {project.services.map((service, index) => (
                    <Chip 
                      key={index}
                      label={`#${service}`}
                      sx={{ 
                        bgcolor: '#1976d2',
                        color: 'white',
                        fontWeight: 500
                      }}
                    />
                  ))}
                </Box>
              </Paper>
            )}

            {/* Document Status */}
            <Paper sx={{ p: 2.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 2, display: 'block' }}>
                DOCUMENT STATUS
              </Typography>
              <List sx={{ p: 0 }}>
                {project.documents && project.documents.map((doc, index) => (
                  <ListItem 
                    key={index}
                    sx={{ 
                      px: 0, 
                      py: 1.5,
                      borderBottom: index < project.documents.length - 1 ? '1px solid #f0f0f0' : 'none'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {getDocumentStatusIcon(doc.status)}
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {doc.name}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {doc.status === 'approved' && doc.url && (
                          <Tooltip title="View Document">
                            <IconButton
                              size="small"
                              onClick={() => {
                                // Construct full URL if it's a relative path
                                const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
                                const backendUrl = baseUrl.replace('/api/v1', '');
                                const fullUrl = doc.url.startsWith('http') ? doc.url : `${backendUrl}${doc.url}`;
                                setViewingDocument({ name: doc.name, url: fullUrl });
                                setDocumentViewerOpen(true);
                              }}
                              sx={{
                                color: '#1976d2',
                                '&:hover': {
                                  bgcolor: '#e3f2fd'
                                }
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Chip 
                          label={capitalizeWords(doc.status)}
                          size="small"
                          sx={{
                            bgcolor: doc.status === 'approved' ? '#e8f5e9' : 
                                     doc.status === 'under_review' ? '#fff3e0' : '#ffebee',
                            color: doc.status === 'approved' ? '#2e7d32' :
                                   doc.status === 'under_review' ? '#e65100' : '#c62828',
                            fontWeight: 600
                          }}
                        />
                      </Box>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Box>

          {/* Right Panel - Team Discussion */}
          <Box sx={{ width: '55%', display: 'flex', flexDirection: 'column' }}>
            {/* Chat Header */}
            <Box sx={{ p: 2.5, borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Team Discussion
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {project.consultant_name ? 
                  `Chat with ${capitalizeWords(project.consultant_name)}` : 
                  'No consultant assigned yet'}
              </Typography>
            </Box>

            {/* Messages */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3, bgcolor: '#fafafa' }}>
              {messages.length === 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.secondary">
                    No messages yet. Start the conversation!
                  </Typography>
                </Box>
              ) : (
                messages.map((message) => {
                  const isOwnMessage = message.sender === user?.id;
                  return (
                    <Box
                      key={message.id}
                      sx={{
                        display: 'flex',
                        justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                        mb: 2
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: '70%',
                          p: 2,
                          borderRadius: 2,
                          bgcolor: isOwnMessage ? '#1976d2' : '#ffffff',
                          color: isOwnMessage ? 'white' : '#333',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                        }}
                      >
                        {!isOwnMessage && (
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                            {capitalizeWords(message.sender_name || 'Consultant')}:
                          </Typography>
                        )}
                        <Typography variant="body1">{message.content}</Typography>
                        {message.attachment && (
                          <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 1 }}>
                            <Typography variant="caption">
                              📎 {message.attachment.split('/').pop()}
                            </Typography>
                          </Box>
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 0.5,
                            opacity: 0.8,
                            fontSize: '0.7rem'
                          }}
                        >
                          {new Date(message.created_at).toLocaleString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </Box>

            {/* Message Input */}
            <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', bgcolor: 'white' }}>
              {selectedFile && (
                <Box sx={{ mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="caption">
                    📎 {selectedFile.name}
                  </Typography>
                  <IconButton size="small" onClick={() => setSelectedFile(null)}>
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <input
                  type="file"
                  id="file-upload"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                <label htmlFor="file-upload">
                  <IconButton component="span" sx={{ color: '#666' }}>
                    <AttachFile />
                  </IconButton>
                </label>
                <TextField
                  fullWidth
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  multiline
                  maxRows={3}
                  size="small"
                  sx={{ bgcolor: '#f8f9fa' }}
                />
                <IconButton
                  onClick={handleSendMessage}
                  disabled={(!newMessage.trim() && !selectedFile) || loading}
                  sx={{
                    bgcolor: '#1976d2',
                    color: 'white',
                    '&:hover': { bgcolor: '#1565c0' },
                    '&:disabled': { bgcolor: '#e0e0e0' }
                  }}
                >
                  <Send />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>

    {/* Document Viewer Dialog */}
    <Dialog
      open={documentViewerOpen}
      onClose={() => {
        setDocumentViewerOpen(false);
        setViewingDocument(null);
      }}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh'
        }
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        p: 2,
        borderBottom: '1px solid #e0e0e0',
        bgcolor: 'white'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {viewingDocument?.name || 'Document Preview'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="contained"
            onClick={() => {
              // Force download using fetch and blob
              fetch(viewingDocument?.url)
                .then(response => response.blob())
                .then(blob => {
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = viewingDocument?.name || 'document';
                  document.body.appendChild(a);
                  a.click();
                  window.URL.revokeObjectURL(url);
                  document.body.removeChild(a);
                })
                .catch(error => {
                  console.error('Download failed:', error);
                  // Fallback: open in new tab
                  window.open(viewingDocument?.url, '_blank');
                });
            }}
            sx={{ 
              bgcolor: '#1976d2',
              '&:hover': { bgcolor: '#1565c0' },
              textTransform: 'none'
            }}
          >
            Download
          </Button>
          <IconButton 
            onClick={() => {
              setDocumentViewerOpen(false);
              setViewingDocument(null);
            }}
            sx={{ color: '#666' }}
          >
            <Close />
          </IconButton>
        </Box>
      </Box>
      <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden', bgcolor: '#f5f5f5' }}>
        {viewingDocument && (
          <Box sx={{ width: '100%', height: '100%' }}>
            {viewingDocument.url.toLowerCase().endsWith('.pdf') ? (
              <embed
                src={`${viewingDocument.url}#toolbar=0&navpanes=0&scrollbar=0`}
                type="application/pdf"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                title={viewingDocument.name}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2
                }}
              >
                <img
                  src={viewingDocument.url}
                  alt={viewingDocument.name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999">Unable to load image</text></svg>';
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ProjectDetailsPage;
